// 账号绑定整体保存：每条绑定显式选择已有规则或模板（D131、D150）。
// 优先级由列表顺序表示：保存时按顺序生成 1..n（小数字优先，D132，#363）。
import { rowKey } from "@/app/form";
import type { AccountRouting, AccountRoutingInput, AccountTargetType, RoutingRule, RoutingTemplate } from "@/app/routing";
import type { Errors } from "@/app/useDraft";
import { unconditionalRule } from "./routingBindingValidation";

/**
 * 绑定表各列：序号、目标类型、目标、目标详情（占剩余宽度）、启用（开关宽度）、行末操作（上移、下移、移除三个小按钮的定宽，再多留一级间距与启用列隔开）。
 * 表头与每行各是一个 grid，列宽必须都是定值或 fr（不用 auto），否则两边按各自内容算宽、列会错开（#363）。账号路由页与 /ui 示例共用。
 */
export const BINDING_COLUMNS = "1.5em var(--edit-type-w) var(--edit-target-w) minmax(0, 1fr) var(--control-h) calc(3 * var(--control-h-sm) + 2 * var(--space-1) + var(--space-4))";

/** 稳定key用于前端身份，id仅用于保留服务端绑定。 */
export interface AccountBindingDraft {
  key: number; id: number; target_type: AccountTargetType; rule_id: string; template_id: string; enabled: boolean;
}
/** 捕获账号路由版本，不跟随后台刷新变更。 */
export interface AccountRoutingDraft { version: number; bindings: AccountBindingDraft[] }

/** 新增绑定默认启用，追加在末尾即最低优先。 */
export function newAccountBinding(): AccountBindingDraft { return { key: rowKey(), id: 0, target_type: "rule", rule_id: "", template_id: "", enabled: true }; }

/** 初始按小数字优先排列成列表顺序，原优先级数值不再保留；绑定身份与并发令牌保留。 */
export function accountRoutingDraft(row: AccountRouting): AccountRoutingDraft {
  return { version: row.version, bindings: [...row.bindings].sort((a, b) => a.priority - b.priority).map(b => ({
    key: rowKey(), id: b.id, target_type: b.target_type, rule_id: b.rule_id ? String(b.rule_id) : "", template_id: b.template_id ? String(b.template_id) : "", enabled: b.enabled,
  })) };
}

/** 规则型绑定引用的已有规则，用于摘要与兜底验证；模板型绑定没有规则。 */
export function accountBindingRule(b: AccountBindingDraft, rules: RoutingRule[]): RoutingRule | undefined {
  if (b.target_type === "template") return undefined;
  return rules.find(r => String(r.id) === b.rule_id);
}

/** 模板型绑定选中的模板，规则型绑定返回 undefined。 */
export function accountBindingTemplate(b: AccountBindingDraft, templates: RoutingTemplate[]): RoutingTemplate | undefined {
  return b.target_type === "template" ? templates.find(t => String(t.id) === b.template_id) : undefined;
}

/** 一行的校验错误：field 区分"目标未选"（标红目标下拉）与"位置不对"（只在行内提示）。 */
export interface BindingError { field: "target" | "order"; message: string }

/**
 * 逐行校验，结果与 bindings 下标一一对应，供行内提示（#363）。
 * 无条件规则之后的绑定不可达，停用也不豁免（D132），错误标在该规则所在行，移动后实时重算；
 * 目标未选只对 checked 里的行提示（点保存时已存在的行），避免之后新加的空行立即报错。
 */
export function accountBindingErrors(bindings: AccountBindingDraft[], rules: RoutingRule[], templates: RoutingTemplate[], checked: ReadonlySet<number>): (BindingError | undefined)[] {
  return bindings.map((b, i): BindingError | undefined => {
    const rule = accountBindingRule(b, rules);
    if (b.target_type === "template" ? !accountBindingTemplate(b, templates) : !rule) {
      return checked.has(b.key) ? { field: "target", message: b.target_type === "template" ? "请选择路由模板。" : "请选择路由规则。" } : undefined;
    }
    if (rule && i < bindings.length - 1 && unconditionalRule(rule.match)) return { field: "order", message: "无条件兜底只能是最后一条，请下移或移除其后的绑定。" };
  });
}

/** 整组转换，按列表顺序生成优先级 1..n。 */
export function accountRoutingInput(d: AccountRoutingDraft, rules: RoutingRule[], templates: RoutingTemplate[] = []): { body?: AccountRoutingInput; errors: Errors<AccountRoutingDraft> } {
  const rowErrors = accountBindingErrors(d.bindings, rules, templates, new Set(d.bindings.map(b => b.key)));
  const first = rowErrors.findIndex(Boolean);
  if (first >= 0) return { errors: { bindings: `第 ${first + 1} 条${rowErrors[first]!.message}` } };
  return { errors: {}, body: { version: d.version, bindings: d.bindings.map((b, i) => ({ id: b.id, target_type: b.target_type, priority: i + 1, enabled: b.enabled,
    ...(b.target_type === "template" ? { template_id: Number(b.template_id) } : { rule_id: Number(b.rule_id) }) })) } };
}
